import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageCircle, Send, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/empty-state';
import { useChatThreads, useChatMessages, useSendMessage } from '@/features/mercado/api';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { cn } from '@/lib/utils/cn';

// Mock current user ID — matches the one in mercado-handlers
const CURRENT_USER_ID = '01JBN5K7M3RSDNT00000000001';

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'ahora';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

/* -------------------------------------------------------------------------- */
/* Thread list panel                                                           */
/* -------------------------------------------------------------------------- */

interface ThreadListPanelProps {
  activeThreadId?: string;
  onSelect: (threadId: string) => void;
}

function ThreadListPanel({ activeThreadId, onSelect }: ThreadListPanelProps) {
  const { data, isLoading } = useChatThreads();
  const threads = data?.items ?? [];

  return (
    <div className="flex h-full flex-col border-r border-[var(--color-hairline)]">
      {/* Header */}
      <div className="shrink-0 border-b border-[var(--color-hairline)] px-4 py-4">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-widest text-[var(--color-ink-faint)]">
          Comunidad · Mercado Vecinal
        </p>
        <h1 className="mt-1 text-xl font-semibold text-[var(--color-ink)]">
          Mensajes
        </h1>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-0 divide-y divide-[var(--color-hairline)]">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        ) : threads.length === 0 ? (
          <div className="flex h-full items-center justify-center p-6">
            <EmptyState
              title="Sin conversaciones"
              description="Cuando contactes a un vendedor, la conversacion aparecera aqui."
            />
          </div>
        ) : (
          <div className="divide-y divide-[var(--color-hairline)]">
            {threads.map((thread) => {
              const isActive = thread.id === activeThreadId;
              return (
                <button
                  key={thread.id}
                  type="button"
                  onClick={() => onSelect(thread.id)}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-3.5 text-left',
                    'transition-colors duration-100',
                    isActive
                      ? 'bg-[var(--color-primary)]/8'
                      : 'hover:bg-[var(--color-paper-dim)]',
                    'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-primary)]',
                  )}
                >
                  {/* Listing thumbnail */}
                  {thread.listingImageUrl ? (
                    <img
                      src={thread.listingImageUrl}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded object-cover"
                    />
                  ) : (
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded bg-[var(--color-paper-dim)]">
                      <MessageCircle className="h-4 w-4 text-[var(--color-ink-faint)]" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-1">
                      <p className={cn(
                        'truncate text-sm',
                        thread.unreadCount > 0 ? 'font-semibold text-[var(--color-ink)]' : 'font-medium text-[var(--color-ink)]',
                      )}>
                        {thread.otherPartyName}
                      </p>
                      <span className="shrink-0 text-[0.625rem] text-[var(--color-ink-faint)]">
                        {timeAgo(thread.lastMessageAt)}
                      </span>
                    </div>
                    <p className="truncate text-xs text-[var(--color-ink-muted)]">
                      {thread.listingTitle}
                    </p>
                    <p className={cn(
                      'truncate text-xs',
                      thread.unreadCount > 0 ? 'font-medium text-[var(--color-ink)]' : 'text-[var(--color-ink-faint)]',
                    )}>
                      {thread.lastMessagePreview}
                    </p>
                  </div>

                  {/* Unread badge */}
                  {thread.unreadCount > 0 && (
                    <span className="grid h-4.5 w-4.5 shrink-0 place-items-center rounded-full bg-[var(--color-accent)] text-[0.5rem] font-bold text-white">
                      {thread.unreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Conversation panel                                                          */
/* -------------------------------------------------------------------------- */

interface ConversationPanelProps {
  threadId: string;
  onBack?: () => void;
}

function ConversationPanel({ threadId, onBack }: ConversationPanelProps) {
  const [input, setInput] = React.useState('');
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  const { data: threadsData } = useChatThreads();
  const thread = threadsData?.items.find((t) => t.id === threadId);

  const { data, isLoading } = useChatMessages(threadId);
  const sendMessage = useSendMessage(threadId);
  const messages = data?.items ?? [];

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim()) return;
    const content = input.trim();
    setInput('');
    await sendMessage.mutateAsync({ content });
  }

  return (
    <div className="flex h-full flex-col">
      {/* Conversation header */}
      <div className="flex shrink-0 items-center gap-3 border-b border-[var(--color-hairline)] px-4 py-3.5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mr-1 rounded p-1 text-[var(--color-ink-muted)] hover:bg-[var(--color-paper-dim)] hover:text-[var(--color-ink)]"
            aria-label="Volver a conversaciones"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        {thread?.listingImageUrl ? (
          <img
            src={thread.listingImageUrl}
            alt=""
            className="h-9 w-9 shrink-0 rounded object-cover"
          />
        ) : (
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded bg-[var(--color-paper-dim)]">
            <MessageCircle className="h-4 w-4 text-[var(--color-ink-faint)]" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[var(--color-ink)]">
            {thread?.otherPartyName ?? '...'}
          </p>
          <p className="truncate text-xs text-[var(--color-ink-faint)]">
            {thread?.listingTitle ?? ''}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={cn('flex gap-2', i % 2 === 0 ? '' : 'justify-end')}>
                <Skeleton className="h-7 w-7 rounded-full" />
                <Skeleton className="h-12 w-44 rounded-lg" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => {
              const isMe = msg.senderId === CURRENT_USER_ID;
              const time = new Date(msg.createdAt).toLocaleTimeString('es-CL', {
                hour: '2-digit',
                minute: '2-digit',
              });

              return (
                <div
                  key={msg.id}
                  className={cn('flex gap-2', isMe ? 'flex-row-reverse' : '')}
                >
                  <Avatar className="h-6 w-6 shrink-0 text-[0.45rem]">
                    <AvatarFallback>{initials(msg.senderName)}</AvatarFallback>
                  </Avatar>
                  <div
                    className={cn(
                      'max-w-[72%] rounded-xl px-3 py-2',
                      isMe
                        ? 'rounded-tr-sm bg-[var(--color-primary)] text-[var(--color-paper)]'
                        : 'rounded-tl-sm border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] text-[var(--color-ink)]',
                    )}
                  >
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                    <p
                      className={cn(
                        'mt-0.5 text-right text-[0.6rem]',
                        isMe
                          ? 'text-[var(--color-paper)]/60'
                          : 'text-[var(--color-ink-faint)]',
                      )}
                    >
                      {time}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input bar */}
      <form
        onSubmit={handleSend}
        className="flex shrink-0 items-center gap-2 border-t border-[var(--color-hairline)] px-4 py-3"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe un mensaje..."
          className={cn(
            'flex-1 rounded-full border border-[var(--color-hairline-strong)] bg-[var(--color-paper-dim)] px-4 py-2 text-sm text-[var(--color-ink)]',
            'placeholder:text-[var(--color-ink-faint)] transition-all duration-150',
            'focus:border-[var(--color-primary)] focus:bg-[var(--color-paper)] focus:outline-none',
            'focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_12%,transparent)]',
          )}
        />
        <Button
          type="submit"
          variant="primary"
          size="icon"
          disabled={!input.trim() || sendMessage.isPending}
          aria-label="Enviar mensaje"
          className="shrink-0 rounded-full"
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty right panel                                                           */
/* -------------------------------------------------------------------------- */

function EmptyConversationPanel() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-2xl border border-[var(--color-hairline)] bg-[var(--color-paper-dim)]">
        <MessageCircle className="h-6 w-6 text-[var(--color-ink-faint)]" />
      </div>
      <div>
        <p className="text-sm font-medium text-[var(--color-ink)]">
          Selecciona una conversacion
        </p>
        <p className="mt-0.5 text-xs text-[var(--color-ink-faint)]">
          Elige un chat de la lista para leer y responder mensajes.
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main layout (used for both /chats and /chats/:threadId)                    */
/* -------------------------------------------------------------------------- */

export function ChatsListRoute() {
  const { threadId } = useParams<{ threadId?: string }>();
  const navigate = useNavigate();
  const { condoId } = useCondoContext();
  const [mobileView, setMobileView] = React.useState<'list' | 'conversation'>(
    threadId ? 'conversation' : 'list',
  );

  // Keep mobile view in sync when URL changes
  React.useEffect(() => {
    setMobileView(threadId ? 'conversation' : 'list');
  }, [threadId]);

  function handleSelect(id: string) {
    navigate(`/app/condominios/${condoId}/mercado/chats/${id}`);
  }

  function handleBack() {
    navigate(`/app/condominios/${condoId}/mercado/chats`);
  }

  return (
    // Full height minus topbar (64px)
    <div
      className="flex overflow-hidden"
      style={{ height: 'calc(100vh - 64px)' }}
    >
      {/* Left column — thread list (always visible on desktop, conditional on mobile) */}
      <div
        className={cn(
          'w-full shrink-0 md:w-[300px] lg:w-[340px]',
          // Mobile: hide when viewing a conversation
          mobileView === 'conversation' ? 'hidden md:block' : 'block',
        )}
      >
        <ThreadListPanel
          activeThreadId={threadId}
          onSelect={handleSelect}
        />
      </div>

      {/* Right column — conversation or empty state */}
      <div
        className={cn(
          'min-w-0 flex-1 bg-[var(--color-paper)]',
          // Mobile: hide when viewing the list
          mobileView === 'list' ? 'hidden md:block' : 'block',
        )}
      >
        {threadId ? (
          <ConversationPanel
            key={threadId}
            threadId={threadId}
            onBack={handleBack}
          />
        ) : (
          <EmptyConversationPanel />
        )}
      </div>
    </div>
  );
}